import { useEffect, useRef } from 'react'
import { notice } from '@/components/pbo/notice'
import { apiErrorText } from '@/components/pbo/apiErrorText'

export const QUERY_LOAD_FAILED_MESSAGE = '데이터를 가져올 수 없습니다. 다시 시도해 주세요.'

/**
 * BE meta.message → detail → message 순. 옛 전역 알럿(QueryCache.onError → Utils.getApiErrMessage)과 **같은 순서**라
 * 같은 조회 실패에 이전과 같은 문구가 나온다(detail = Spring 검증 오류 포맷, message = 비표준 게이트웨이 응답).
 * Utils.getApiErrMessage 를 그대로 못 쓰는 건 HTML 별표(<span>)를 붙여 토스트에 태그가 보이기 때문이다.
 */
/** 서버 문구 추출은 apiErrorText 하나로 — 이제 알럿(pre-line)이라 여러 건은 줄바꿈으로 잇는다. */
const toMessage = apiErrorText

/**
 * pbo-core 화면의 조회 실패를 새 디자인 알럿(notice.failure)으로 알린다 — 전역 QueryCache 알럿(옛 mwms-ds AlertModal)을 대신한다.
 * (2.0 안내 기준 2026-10-02: 실패=알럿. 훅 이름의 Toast 는 옛 이름 그대로다.)
 * 쿼리에는 `meta: { noErrorMessage: true }` 를 함께 달아 전역 알럿이 겹쳐 뜨지 않게 한다.
 * errorUpdatedAt 기준이라 같은 실패로 리렌더돼도 한 번, 재조회가 다시 실패하면 다시 알린다.
 * **재조회 중(isFetching)에는 알리지 않는다** — 조회 조건이 A→B→A 로 돌아오거나 화면에 다시 들어오면 react-query 가 캐시에 남은
 *  예전 실패(error·errorUpdatedAt)를 그대로 돌려준 채 재조회를 시작한다. 그걸 알리면 응답 전에 옛 실패가 한 번, 재조회가 또 실패하면
 *  한 번 더 — 클릭 한 번에 두 번 뜬다(리뷰 gyoungjun). 실패가 확정된(isFetching=false) 것만, 마지막으로 알린 실패 시각과 다를 때만 알린다.
 * fallback 은 실패가 난 시점의 값으로 쓰인다 — 같은 실패가 유지되는 동안 fallback 이 바뀌어도 토스트를 다시 띄우지 않는다.
 * (호출처는 모두 상수 문구다. 문구가 동적으로 바뀔 때마다 다시 알려야 하면 그 소비처가 따로 감지해야 한다.)
 *
 * `preferFallback: true` — BE 문구를 쓰지 않고 항상 fallback 을 띄운다. 무엇이 실패했고 어떻게 하라는지가 화면 문구에만
 * 있을 때 쓴다(예: 화주 목록 — "화주 목록을 불러오지 못했습니다. 새로고침…". BE 의 '일시적인 오류' 로 덮이면 셀렉트가 왜 비었는지 모른다).
 */
export const usePboQueryErrorToast = (
  query: { error: unknown; errorUpdatedAt: number; isFetching: boolean },
  fallback = QUERY_LOAD_FAILED_MESSAGE,
  options: { preferFallback?: boolean } = {},
): void => {
  const { error, errorUpdatedAt, isFetching } = query
  const lastShownAt = useRef<number | null>(null)
  useEffect(() => {
    if (!error || isFetching || lastShownAt.current === errorUpdatedAt) return
    lastShownAt.current = errorUpdatedAt
    notice.failure(options.preferFallback ? fallback : toMessage(error, fallback))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 실패 확정 시각·조회 상태가 바뀔 때만 본다(error 객체 참조·fallback·options 변화로는 다시 띄우지 않는다)
  }, [errorUpdatedAt, isFetching])
}
